<template>
  <div class="trials-list">
    <Breadcrumbs :items="breadcrumbs">
      <rp-permissions :requireAll="['write_trials', 'write_trial_patients']">
        <router-link :to="{name: 'edit-study', params: {id: this.trialId}}">
          <rp-sprite class="create-icon" name="create"/>
        </router-link>
      </rp-permissions>
    </Breadcrumbs>

    <rp-trialtable
      :rows="rows"
      :pageInfo="pageInfo"
      :singleTrialView="true"
      @onExport="onExport"
      @onExtend="onExtend"
      @onClose="onClose" />
    <h3>Surgeons Enrolled</h3>
    <rp-trialsurgeontable
      :trialSurgeons="trialSurgeons"
      @onView="onViewSurgeon"
      @onRemove="onRemoveSurgeon" />

    <rp-overlay :active="showExtend">
      <rp-extendtrial
        v-model="extendTrial"
        @submit="submitExtend"
        @cancel="cancelExtend" />
    </rp-overlay>

    <rp-overlay :active="showConfirmRemove">
      <rp-confirm
        icon="warning_rounded"
        :messageText="confirmRemoveMessage"
        :confirm="doRemoveSurgeon"
        :cancel="closeConfirm" />
    </rp-overlay>

    <rp-overlay :active="showConfirmClose">
      <rp-confirm
        icon="warning_rounded"
        :messageText="confirmCloseMessage"
        :confirm="doClose"
        :cancel="closeConfirm" />
    </rp-overlay>

    <rp-overlay :active="showSuccess">
      <rp-alert
        icon="raypro_icon"
        :message="successMessage"
        :onClick="closeSuccess" />
    </rp-overlay>

  </div>
</template>

<script>
import page from '@/mixins/page'
import Breadcrumbs from '@/components/Breadcrumbs'
import Permissions from 'raypro_ui/src/components/Permissions'
import Sprite from 'raypro_ui/src/components/ui/Sprite'
import Button from 'raypro_ui/src/components/ui/inputs/Button'
import Overlay from 'raypro_ui/src/components/ui/Overlay'
import Confirm from 'raypro_ui/src/components/ui/DeleteConfirm'
import TrialTable from 'raypro_ui/src/components/ui/TrialTable'
import TrialSurgeonTable from 'raypro_ui/src/components/ui/TrialSurgeonTable'
import ExtendTrial from 'raypro_ui/src/components/ui/forms/ExtendTrial'
import {
  getTrial,
  updateTrial,
  getTrialSurgeons,
  exportTrialCsv,
  adminExportTrialCsv,
  deleteTrialSurgeon,
  closeTrial
} from 'raypro_ui/src/api/trials'
import {mapGetters} from 'vuex'

export default {
  mixins: [page],
  components: {
    'rp-button': Button,
    'rp-overlay': Overlay,
    'rp-confirm': Confirm,
    'rp-sprite': Sprite,
    'rp-trialtable': TrialTable,
    'rp-trialsurgeontable': TrialSurgeonTable,
    'rp-extendtrial': ExtendTrial,
    'rp-permissions': Permissions,
    Breadcrumbs
  },

  data() {
    return {
      id: '',
      rows: [],
      pageInfo: {
        page_size: 10,
        page_number: 1
      },
      trialSurgeons: [],
      title: 'View Study',
      breadcrumbs: [
        {label: 'View Study'}
      ],
      showConfirmRemove: false,
      confirmRemoveMessage: '',
      showConfirmClose: false,
      confirmCloseMessage: '',
      delSurgeonId: '',
      showSuccess: false,
      successMessage: '',
      showExtend: false,
      extendTrial: {}
    }
  },

  computed: {
    ...mapGetters(['authUser', 'isAdmin']),

    trialId() {
      return this.$route.params.id
    }
  },

  async mounted() {
    await this.getTrial(this.trialId)
    await this.getTrialSurgeons(this.trialId)
  },

  methods: {
    async getTrial(trialId) {
      let {data} = await getTrial(trialId)
      this.title = data.name
      this.breadcrumbs[0].label = `View Study - ${data.name}`
      this.rows = [data]
    },

    async getTrialSurgeons(trialId) {
      let {data} = await getTrialSurgeons(trialId)
      this.trialSurgeons = data
    },

    async onExport(row) {
      let data = null
      if (this.isAdmin) {
        data = await adminExportTrialCsv(row.id)
      } else {
        data = await exportTrialCsv(row.id, this.authUser.id)
      }

      this.downloadResult(data)
    },

    onExtend(row) {
      this.showExtend = true
    },

    onClose(row) {
      this.confirmCloseMessage = `Are you sure you want to close study ${row.name}?`
      this.showConfirmClose = true
    },

    async doClose() {
      this.showConfirmClose = false
      await closeTrial(this.trialId)
      await this.getTrial(this.trialId)
    },

    async submitExtend(payload) {
      this.showExtend = false
      await updateTrial(this.trialId, {trial: payload})
      await this.getTrial(this.trialId)
      this.extendTrial = {}
    },

    cancelExtend() {
      this.showExtend = false
      this.extendTrial = {}
    },

    onViewSurgeon(row) {
      this.$router.push({name: 'view-surgeon-trials', params: {id: row.id}})
    },

    onRemoveSurgeon(row) {
      this.delSurgeonId = row.id
      this.confirmRemoveMessage = `Are you sure you want to remove ${row.name} from study ${this.rows[0].name}?`
      this.showConfirmRemove = true
    },

    async doRemoveSurgeon() {
      this.showConfirmRemove = false
      await deleteTrialSurgeon(this.trialId, this.delSurgeonId)
      await this.getTrialSurgeons(this.trialId)
    },

    closeConfirm() {
      this.showConfirmRemove = false
      this.showConfirmClose = false
    },

    closeSuccess() {
      this.showSuccess = false
    },

    downloadResult(result) {
      const disposition = result.headers['content-disposition']
      const matches = /"([^"]*)"/.exec(disposition)
      const filename = matches !== null && matches[1] ? matches[1] : 'file.csv'
      const blob = new Blob([result.data], {type: 'text/csv'})
      const link = document.createElement('a')

      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style lang="sass" scoped>
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'
</style>



// WEBPACK FOOTER //
// src/components/pages/Clinical/Trial.vue